
import './index.css';

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { EditIcon } from '../../ui/AppIcon';
import StatusBadge from '../../ui/StatusBadge';

import RecordSection from '../../view/RecordSection';
import RecordHealthSection from '../RecordHealthSection';
import RecordHistorySection from '../RecordHistorySection';
import SprintPackForecastSection from '../SprintPackForecastSection';
import MetricTrendSection from '../MetricTrendSection';
import RecordLinksSection from '../RecordLinksSection';
import RecordApprovalsSection from '../RecordApprovalsSection';

const typeKey = 'record-view-header';

const RecordViewSection = ({
  error,
  slug,
  header = {},
  data = {},
  canUpdateContent = false,
  children = null,
}) => {

  const currentStatus = data.status.value ?? data.record.data.status ?? '';
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);
  const [isEditingStatus, setIsEditingStatus] = useState(false);

  const resolvedEditTo = header.editTo ?? (slug && data.record.data.id ? `/${slug}/${data.record.data.id}/edit` : null);
  const hasActions = Boolean(header.actions || (canUpdateContent && resolvedEditTo));
  const hasStatus = Boolean(data.hasStatus);

  const canEditStatus = Boolean(canUpdateContent && data.status.onSubmit && data.status.options.length);

  useEffect(() => {
    setSelectedStatus(currentStatus);
  }, [currentStatus]);

  function openStatusEdit() {
    if (!canEditStatus) return;
    setSelectedStatus(currentStatus);
    setIsEditingStatus(true);
  }

  function cancelStatusEdit() {
    setSelectedStatus(currentStatus);
    setIsEditingStatus(false);
  }

  async function handleStatusSubmit(event) {
    event.preventDefault();
    if (!canEditStatus || selectedStatus === currentStatus) {
      setIsEditingStatus(false);
      return;
    }

    await data.status.onSubmit(selectedStatus);
    setIsEditingStatus(false);
  }

  return (
    <div className="stack">

      <header className={['page-header', typeKey, header.className].filter(Boolean).join(' ')}>

        {data.status.error ? <p className="error-text">{data.status.error}</p> : null}

        <div className={`${typeKey}-bar`}>

          {hasStatus ? (
            <div className="record-status">

              {isEditingStatus ? (
                <form className="record-status-form" onSubmit={handleStatusSubmit}>
                  <select
                    className="record-status-select"
                    value={selectedStatus}
                    onChange={(event) => setSelectedStatus(event.target.value)}
                    disabled={data.status.saving}
                    autoFocus
                  >
                    {data.status.options.map((option) => {
                      const optionValue = typeof option === 'string' ? option : option.value;
                      const optionLabel = typeof option === 'string' ? option : option.label || option.value;
                      return (
                        <option key={optionValue} value={optionValue}>
                          {optionLabel}
                        </option>
                      );
                    })}
                  </select>

                  <button
                    className="button-primary"
                    type="submit"
                    disabled={data.status.saving}
                  >
                    {data.status.saving ? 'Saving...' : 'Save'}
                  </button>

                  <button
                    className="button-secondary"
                    type="button"
                    onClick={cancelStatusEdit}
                    disabled={data.status.saving}
                  >
                    Cancel
                  </button>
                </form>
              ) : (
                <button
                  className={['record-status-toggle', canEditStatus ? 'record-status-toggle-editable' : ''].filter(Boolean).join(' ')}
                  type="button"
                  onClick={openStatusEdit}
                  title={canEditStatus ? 'Click to update status' : undefined}
                  disabled={!canEditStatus}
                >
                  <StatusBadge value={currentStatus} />
                </button>
              )}
            </div>
          ) : null}

          {hasActions ? (
            <div className={['action-cluster', `${typeKey}-actions`].filter(Boolean).join(' ')}>
              {header.actions}
              {canUpdateContent && resolvedEditTo ? (
                <Link className="button-secondary" to={resolvedEditTo}>
                  <EditIcon /> Edit
                </Link>
              ) : null}
            </div>
          ) : null}

        </div>

        {(() => {

          const Components = require( '../../../elements' ).default;

          const Component = Components.Data.identity( data.handle.value );
          
          return (
            <div className={`${typeKey}-copy`}>
              <h1 className="page-title">
                <Component.Output handle={data.handle.handle} meta={data.handle.meta} record={data.record} />
              </h1>
              {data.handle.label ? <p className="page-subtitle">{data.handle.label}</p> : null}
            </div>
          );
        })()}

      </header>


      <RecordSection title="Details" className="record-details">
        {data.details.length ? (
          <div className="record-details-grid">
            {data.details.map((detail, index) => {

              const Components = require( '../../../elements' ).default;

              const Component = Components.Data.identity( detail.value );
              
              return (
                <div
                  key={detail.key || index}
                  className={Component.meta.wide ? 'record-details-item-wide' : ''}
                >
                  <span className="kv-label">{detail.label}</span>
                  <div>
                    <Component.Output handle={detail.handle} meta={detail.meta} record={data.record} />
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </RecordSection>

      {data.record.data.id ? <RecordHealthSection section={slug} id={data.record.data.id} /> : null}

      {data.record.data.id && data.status.approval ? (
        <RecordApprovalsSection
          slug={slug}
          id={data.record.data.id}
          offer={data.status.approval.offer}
          onOfferHandled={data.status.approval.onOfferHandled}
          onApplied={data.status.approval.onApplied}
        />
      ) : null}

      {data.record.data.id && slug ? (
        <RecordLinksSection slug={slug} id={data.record.data.id} canUpdateContent={canUpdateContent} />
      ) : null}

      {slug === 'sprint-packs' && data.record.data.id ? (
        <SprintPackForecastSection section={slug} id={data.record.data.id} />
      ) : null}

      {slug?.includes('stat-tracker') && data.record.data.sprintPackId && data.record.data.metricKey ? (
        <MetricTrendSection
          sprintPackId={data.record.data.sprintPackId}
          metricKey={data.record.data.metricKey}
          metricLabel={data.record.data.metricLabel}
          unit={data.record.data.unit}
        />
      ) : null}


      {children}

      {data.record.data.id ? <RecordHistorySection section={slug} id={data.record.data.id} /> : null}

    </div>
  );
};

export default RecordViewSection;
